import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PaginatedResponse } from '../models/paginatedResponse';
import {
  Issue,
  IssueListItem,
  IssueComment,
  IssueAttachment,
  IssueCategory,
  IssueSubCategory,
  IssueDeveloperNotes
} from '../models/issue';
import {
  IssueCreateRequest,
  IssueUpdateRequest,
  IssueStatusChangeRequest,
  IssueCommentCreateRequest,
  IssueCommentUpdateRequest,
  IssueDeveloperNotesRequest,
  IssueSearchRequest,
  IssueCategoryRequest,
  IssueSubCategoryRequest
} from '../models/api/issueRequests';

/**
 * Snapshot of the issue list's filters, sort and pagination. Held on the
 * root-provided service so it survives navigating into an issue and back,
 * restoring the user's view instead of resetting to defaults.
 */
export interface IssueListState {
  filterStatuses: string[];
  filterType: string;
  filterCategoryId: string;
  filterPriority: string;
  filterVerified: string;
  filterSearch: string;
  currentPage: number;
  pageSize: number;
  sortBy: string;
  ascending: boolean;
}

@Injectable({ providedIn: 'root' })
export class IssueService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/issues`;
  private categoryUrl = `${environment.apiUrl}/issue-categories`;

  /** Last list view state; `null` until the user has visited the list once. */
  listState: IssueListState | null = null;

  // --- Issues ---------------------------------------------------------------

  search(request: IssueSearchRequest): Observable<PaginatedResponse<IssueListItem>> {
    return this.http.post<PaginatedResponse<IssueListItem>>(`${this.apiUrl}/search`, request);
  }

  getById(id: string): Observable<Issue> {
    return this.http.get<Issue>(`${this.apiUrl}/${id}`);
  }

  create(request: IssueCreateRequest): Observable<Issue> {
    return this.http.post<Issue>(this.apiUrl, request);
  }

  update(id: string, request: IssueUpdateRequest): Observable<Issue> {
    return this.http.put<Issue>(`${this.apiUrl}/${id}`, request);
  }

  changeStatus(id: string, request: IssueStatusChangeRequest): Observable<Issue> {
    return this.http.post<Issue>(`${this.apiUrl}/${id}/status`, request);
  }

  addComment(id: string, request: IssueCommentCreateRequest): Observable<IssueComment> {
    return this.http.post<IssueComment>(`${this.apiUrl}/${id}/comments`, request);
  }

  /** Author exception: edit your own comment. The server rejects anyone else. */
  updateComment(id: string, commentId: string, request: IssueCommentUpdateRequest): Observable<IssueComment> {
    return this.http.put<IssueComment>(`${this.apiUrl}/${id}/comments/${commentId}`, request);
  }

  /** Author exception: delete your own comment. The server rejects anyone else. */
  deleteComment(id: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/comments/${commentId}`);
  }

  // --- Developer notes (Keycloak `developer` realm role only) ----------------

  getDeveloperNotes(id: string): Observable<IssueDeveloperNotes> {
    return this.http.get<IssueDeveloperNotes>(`${this.apiUrl}/${id}/developer-notes`);
  }

  saveDeveloperNotes(id: string, request: IssueDeveloperNotesRequest): Observable<IssueDeveloperNotes> {
    return this.http.put<IssueDeveloperNotes>(`${this.apiUrl}/${id}/developer-notes`, request);
  }

  addAttachment(id: string, file: File): Observable<IssueAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<IssueAttachment>(`${this.apiUrl}/${id}/attachments`, formData);
  }

  downloadAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/attachments/${attachmentId}`, { responseType: 'blob' });
  }

  /**
   * Tester exception: confirm the fix works. Needs only Issues visibility; the server records who,
   * when, and which environment (derived from the issue's status) without moving the issue.
   */
  verify(id: string): Observable<Issue> {
    return this.http.post<Issue>(`${this.apiUrl}/${id}/verify`, {});
  }

  /**
   * Tester exception: the fix does not work. Sends the issue back to In Progress. It carries no note —
   * what is wrong goes in a comment, where it is readable at length and can be replied to.
   */
  verificationFailed(id: string): Observable<Issue> {
    return this.http.post<Issue>(`${this.apiUrl}/${id}/verification-failed`, {});
  }

  /** Reporter exception: withdraw your own issue while it is still Open. */
  withdraw(id: string): Observable<Issue> {
    return this.http.post<Issue>(`${this.apiUrl}/${id}/withdraw`, {});
  }

  /** Reporter exception: delete your own issue while it is still Open. */
  deleteOwn(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/mine`);
  }

  /** Developer delete (any state). */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // --- Categories -----------------------------------------------------------

  getCategories(): Observable<IssueCategory[]> {
    return this.http.get<IssueCategory[]>(this.categoryUrl);
  }

  createCategory(request: IssueCategoryRequest): Observable<IssueCategory> {
    return this.http.post<IssueCategory>(this.categoryUrl, request);
  }

  updateCategory(id: string, request: IssueCategoryRequest): Observable<IssueCategory> {
    return this.http.put<IssueCategory>(`${this.categoryUrl}/${id}`, request);
  }

  deleteCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.categoryUrl}/${id}`);
  }

  createSubCategory(request: IssueSubCategoryRequest): Observable<IssueSubCategory> {
    return this.http.post<IssueSubCategory>(`${this.categoryUrl}/sub`, request);
  }

  updateSubCategory(id: string, request: IssueSubCategoryRequest): Observable<IssueSubCategory> {
    return this.http.put<IssueSubCategory>(`${this.categoryUrl}/sub/${id}`, request);
  }

  deleteSubCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.categoryUrl}/sub/${id}`);
  }
}
